import { supabase } from "@/integrations/supabase/client";

/**
 * Free plan: exactly ONE fully committed loan per family/user account.
 * Drafts and saved (uncommitted) scenarios are unlimited.
 * Enforcement is server-side via the `enforce_commitment_limit` trigger;
 * these helpers only drive a calm UI experience.
 */
export const COMMITMENT_LIMIT_MESSAGE =
  "You’ve completed your first Family Banking Commitment Agreement™. Continue building your family financing history with a paid plan.";

/** Raised by the database trigger when a second commitment is attempted. */
export const COMMITMENT_LIMIT_CODE = "commitment_limit_reached";

export function isCommitmentLimitError(e: unknown): boolean {
  const msg = e instanceof Error ? e.message : String(e ?? "");
  return msg.includes(COMMITMENT_LIMIT_CODE);
}

export async function hasActiveEntitlement(userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from("account_entitlements")
    .select("id")
    .eq("user_id", userId)
    .eq("active", true)
    .in("plan", ["beta", "paid"])
    .limit(1);
  if (error) return false;
  return (data ?? []).length > 0;
}

export async function committedScenarioCount(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from("scenarios")
    .select("id", { count: "exact", head: true })
    .eq("user_id", userId)
    .eq("status", "committed");
  if (error) return 0;
  return count ?? 0;
}

/** Client-side mirror of `public.can_commit_scenario`. Advisory only. */
export async function canCommitScenario(userId: string): Promise<boolean> {
  if (await hasActiveEntitlement(userId)) return true;
  return (await committedScenarioCount(userId)) < 1;
}